import { useEffect, useState } from "react";
import axios from "axios";
import { fr } from "date-fns/locale";
import { format, addDays, startOfDay } from "date-fns";
import { toast } from "sonner";
import { Phone, Loader2, CalendarCheck, ChevronLeft, CalendarDays } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Calendar } from "../ui/calendar";
import Watcher from "./Watcher";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", email: "", phone: "", note: "" };
const inputClass =
  "w-full bg-[#08080a] border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-zinc-600 outline-none focus:border-blue-500 transition-colors duration-300";

const isClosed = (d) => {
  const day = d.getDay(); // 0 = dimanche, 6 = samedi
  return day === 0 || day === 6 || d < startOfDay(new Date()) || d > addDays(new Date(), 60);
};

const BookingCTA = () => {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState();
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [time, setTime] = useState("");
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (!date) return;
    setTime("");
    setLoadingSlots(true);
    axios
      .get(`${API}/bookings/availability`, { params: { date: format(date, "yyyy-MM-dd") } })
      .then(({ data }) => setSlots(data.slots))
      .catch(() => setSlots([]))
      .finally(() => setLoadingSlots(false));
  }, [date]);

  const submit = async (e) => {
    e.preventDefault();
    if (!date || !time) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/bookings`, { ...form, date: format(date, "yyyy-MM-dd"), time });
      setDone(data);
      toast.success("Appel réservé ! Un courriel de confirmation vous a été envoyé.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible de réserver ce créneau. Réessayez.");
      if (err.response?.status === 409) setDate(new Date(date));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="flex-1 flex flex-col sm:flex-row sm:items-center gap-6" data-testid="booking-success">
        <Watcher mood="done" className="w-24 shrink-0" />
        <div>
          <span className="w-12 h-12 rounded-full bg-blue-500/15 border border-blue-500/30 flex items-center justify-center mb-4">
            <CalendarCheck size={22} className="text-blue-400" />
          </span>
          <h3 className="font-display font-bold text-2xl tracking-tight mb-2">C'est noté, {done.name.split(" ")[0]} !</h3>
          <p className="text-zinc-400 text-sm leading-relaxed max-w-md">
            Je vous appelle le{" "}
            <span className="text-white font-medium">
              {format(new Date(`${done.date}T12:00:00`), "EEEE d MMMM", { locale: fr })} à {done.time}
            </span>{" "}
            (heure du Québec) au {done.phone}. Un récapitulatif vient de partir par courriel.
          </p>
        </div>
      </div>
    );
  }

  const mood = !open ? "idle" : time ? "picked" : "watching";

  return (
    <div className="flex-1" data-testid="booking-section">
      <p className="font-mono text-xs uppercase tracking-[0.25em] text-blue-400 mb-3">Appel découverte</p>
      <h3 className="font-display font-bold text-2xl sm:text-3xl tracking-tight mb-3">Réservons 30 minutes pour en parler</h3>
      <p className="text-zinc-400 text-sm leading-relaxed max-w-md mb-6">
        Une discussion téléphonique gratuite de 30 minutes pour cerner votre projet, vos objectifs et la meilleure façon d'y arriver.
        Disponible du lundi au vendredi, heure du Québec.
      </p>

      <div className="flex flex-col sm:flex-row sm:items-start gap-6">
        <Watcher mood={mood} className="w-20 sm:w-24 shrink-0 self-start sm:mt-2" />

        <AnimatePresence mode="wait" initial={false}>
          {!open && (
            <motion.div key="cta" exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.25 }} className="sm:pt-6">
              <button
                onClick={() => setOpen(true)}
                className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white font-semibold px-7 py-3.5 rounded-full transition-colors duration-300 shadow-[0_0_50px_-12px_rgba(59,130,246,0.5)]"
                data-testid="booking-open-button"
              >
                <CalendarDays size={18} /> Réserver mon appel
              </button>
              <p className="font-mono text-[11px] uppercase tracking-wider text-zinc-600 mt-3">Gratuit · 30 min · Sans engagement</p>
            </motion.div>
          )}

          {open && !time && (
            <motion.div
              key="calendar"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="flex flex-col sm:flex-row gap-6 flex-1 min-w-0"
            >
              <div className="rounded-2xl border border-white/10 bg-[#08080a] self-start" data-testid="booking-calendar">
                <Calendar mode="single" locale={fr} selected={date} onSelect={setDate} disabled={isClosed} weekStartsOn={1} />
              </div>
              <div className="flex-1 min-w-0">
                {!date && (
                  <p className="text-zinc-500 text-sm font-mono uppercase tracking-wider pt-3" data-testid="booking-pick-date-hint">
                    Choisissez une date pour voir les créneaux.
                  </p>
                )}
                {date && loadingSlots && <Loader2 className="animate-spin text-blue-400 mt-3" size={20} />}
                {date && !loadingSlots && (
                  <>
                    <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 mb-3">
                      {format(date, "EEEE d MMMM", { locale: fr })}
                    </p>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="booking-slots">
                      {slots.map((s) => (
                        <button
                          key={s.time}
                          type="button"
                          disabled={!s.available}
                          onClick={() => setTime(s.time)}
                          className="px-2 py-2.5 rounded-lg text-sm font-mono border border-white/10 text-zinc-300 hover:border-blue-500 hover:text-white disabled:opacity-25 disabled:line-through disabled:hover:border-white/10 transition-colors duration-200"
                          data-testid={`booking-slot-${s.time.replace(":", "")}`}
                        >
                          {s.time}
                        </button>
                      ))}
                    </div>
                    {slots.every((s) => !s.available) && (
                      <p className="text-zinc-500 text-xs mt-3">Plus de créneau ce jour-là, essayez une autre date.</p>
                    )}
                  </>
                )}
              </div>
            </motion.div>
          )}

          {open && time && (
            <motion.form
              key="form"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              onSubmit={submit}
              className="max-w-md flex-1"
              data-testid="booking-form"
            >
              <button
                type="button"
                onClick={() => setTime("")}
                className="flex items-center gap-1 text-xs font-mono uppercase tracking-wider text-zinc-500 hover:text-white mb-4 transition-colors"
                data-testid="booking-back-button"
              >
                <ChevronLeft size={14} /> {format(date, "EEEE d MMMM", { locale: fr })} · {time}
              </button>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                <input required minLength={2} placeholder="Votre nom" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputClass} data-testid="booking-name-input" />
                <input required type="tel" placeholder="Téléphone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputClass} data-testid="booking-phone-input" />
              </div>
              <input required type="email" placeholder="Votre courriel" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={`${inputClass} mb-3`} data-testid="booking-email-input" />
              <textarea rows={2} placeholder="En deux mots, votre projet (optionnel)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className={`${inputClass} resize-none mb-4`} data-testid="booking-note-input" />
              <button
                type="submit"
                disabled={submitting}
                className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 disabled:opacity-60 text-white font-semibold px-7 py-3.5 rounded-full transition-colors duration-300 shadow-[0_0_50px_-12px_rgba(59,130,246,0.5)]"
                data-testid="booking-submit-button"
              >
                {submitting ? <Loader2 size={18} className="animate-spin" /> : <Phone size={18} />}
                Confirmer l'appel
              </button>
            </motion.form>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default BookingCTA;
